import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/motion/Reveal";
import { Eyebrow, Assurances } from "@/components/kit";
import {
  Sparkles, ArrowRight, Loader2, CheckCircle2, RefreshCw, TrendingUp,
  AlertTriangle, Target, Zap, Flame, Send, Phone, Building2, Mail, User,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const branches = [
  "E-commerce / Webshop", "Lokaal MKB / Dienstverlening", "B2B / SaaS",
  "Coaching / Training", "Bouw / Installatie", "Horeca / Retail",
  "Zorg / Wellness", "Anders",
];
const doelen = [
  "Meer leads / klanten", "Hogere online conversie", "Betere vindbaarheid (SEO)",
  "Sterker merk online", "Meer omzet uit advertenties", "Schaalbaar fundament bouwen",
];
const uitdagingen = [
  "Website converteert slecht", "Weinig bezoekers / verkeer", "Advertenties leveren niets op",
  "Onvindbaar in Google", "Content werkt niet", "Geen tijd / expertise intern",
];
const situaties = [
  "Nog geen serieuze marketing", "Wat probeersels, geen structuur", "Draaien wel, groei stagneert",
  "Groeien, maar willen versnellen", "Marktleider maar bang voor concurrentie",
];
const urgenties = [
  "Deze maand nog resultaat", "Binnen 60 dagen", "Binnen 90 dagen", "Rustig aan, geen haast",
];
const budgetten = ["< €500/mnd", "€500 - €1.500/mnd", "€1.500 - €5.000/mnd", "€5.000+/mnd"];

const STEPS = [
  { key: "branche", title: "In welke branche zit je?", subtitle: "Zo weten we welke tactieken bij jouw markt passen.", options: branches },
  { key: "doel", title: "Wat is je belangrijkste groeidoel?", subtitle: "Waar wil je over 12 maanden staan?", options: doelen },
  { key: "uitdaging", title: "Wat is je grootste blokkade nu?", subtitle: "Waar loop je écht tegenaan?", options: uitdagingen },
  { key: "huidige_situatie", title: "Waar sta je online vandaag?", subtitle: "Wees eerlijk — daar hangt het advies vanaf.", options: situaties },
  { key: "urgentie", title: "Hoe snel wil je resultaat zien?", subtitle: "Bepaalt of we sprints inzetten of langetermijn bouwen.", options: urgenties },
  { key: "budget", title: "Wat is je maandelijkse marketingbudget?", subtitle: "Inclusief advertentiebudget. Bepaalt wat haalbaar is.", options: budgetten },
];

const initialAnswers = STEPS.reduce((a, s) => ({ ...a, [s.key]: "" }), {});

const AiScan = () => {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [captured, setCaptured] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", company: "", wants_call: true });
  const [capturing, setCapturing] = useState(false);

  const currentStep = STEPS[stepIdx];
  const totalSteps = STEPS.length;
  const progress = result ? 100 : ((stepIdx + 1) / totalSteps) * 100;

  const choose = async (value) => {
    const next = { ...answers, [currentStep.key]: value };
    setAnswers(next);
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai-scan`, next, { timeout: 90000 });
      setResult(data);
    } catch (err) {
      console.error(err);
      toast.error("De AI is even niet bereikbaar. Probeer het zo opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email || !result) {
      toast.error("Vul naam en e-mail in.");
      return;
    }
    setCapturing(true);
    try {
      await axios.post(`${API}/leads`, { ...lead, scan_id: result.id }, { timeout: 30000 });
      setCaptured(true);
      toast.success("Verzonden! Check je mailbox — er komt zo een uitgebreid rapport binnen.");
    } catch (err) {
      console.error(err);
      toast.error("Er ging iets mis. Probeer het opnieuw of mail info@uppit.nl direct.");
    } finally {
      setCapturing(false);
    }
  };

  const reset = () => {
    setStepIdx(0);
    setAnswers(initialAnswers);
    setResult(null);
    setCaptureOpen(false);
    setCaptured(false);
    setLead({ name: "", email: "", phone: "", company: "", wants_call: true });
  };

  const scoreColor = (s) => (s >= 60 ? "text-electric" : s >= 40 ? "text-coal" : "text-signal");
  const scoreLabel = (s) => (s >= 80 ? "Sterk fundament" : s >= 60 ? "Goed op weg" : s >= 40 ? "Groeipotentieel" : "Grote kansen");

  return (
    <section id="ai-scan" data-testid="ai-scan-section" className="relative bg-ivory-200 py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Pitch */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-electric/20 bg-electric/10 px-4 py-1.5 mb-6">
              <Flame className="w-3.5 h-3.5 text-electric" />
              <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-electric-700">Gratis · 2 min · direct advies</span>
            </div>
            <Eyebrow>AI Growth Scan</Eyebrow>
            <h2 className="mt-5 font-display font-black text-coal tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              Twee minuten. Eén <span className="text-gradient-blue">eerlijk plan.</span>
            </h2>
            <p className="mt-6 text-lg text-coal/60 leading-relaxed max-w-md">
              Beantwoord 6 slimme vragen — onze AI-strategist berekent je Growth Score,
              geschatte gemiste maandomzet én een persoonlijk 90-dagen plan.
            </p>

            <div className="mt-8 space-y-3 border-t border-ivory-400 pt-8">
              {[
                { icon: Target, text: "Persoonlijk actieplan met prioriteiten" },
                { icon: TrendingUp, text: "Cijfer over je groeipotentieel" },
                { icon: Zap, text: "Powered by AI · resultaat in ~15 sec" },
              ].map(({ icon: Icon, text }, i) => (
                <div key={i} className="flex items-center gap-3 text-[15px] text-coal/80">
                  <div className="w-9 h-9 rounded-lg bg-electric/10 grid place-items-center shrink-0">
                    <Icon className="w-4 h-4 text-electric" />
                  </div>
                  <span>{text}</span>
                </div>
              ))}
            </div>

            <Assurances testid="aiscan-assurances" className="mt-7" />
          </motion.div>

          {/* Wizard */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="relative rounded-[1.75rem] border border-ivory-400 bg-white p-6 lg:p-9 shadow-card-lift">
              <div className="flex items-center justify-between mb-6">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-coal/50">
                  {result ? "Resultaat" : `Stap ${stepIdx + 1} / ${totalSteps}`}
                </div>
                <div className="w-32 h-1.5 bg-ivory-300 rounded-full overflow-hidden">
                  <motion.div
                    initial={false}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.4 }}
                    className="h-full bg-electric"
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="ai-scan-loading" className="py-16 text-center">
                    <div className="relative w-16 h-16 mx-auto mb-6">
                      <div className="absolute inset-0 rounded-full border-2 border-electric/20" />
                      <Loader2 className="absolute inset-0 m-auto w-8 h-8 text-electric animate-spin" />
                    </div>
                    <p className="font-display text-xl font-bold text-coal mb-2">De AI analyseert je antwoorden…</p>
                    <p className="text-sm text-coal/55">Growth Score en persoonlijk advies komen eraan (~15 sec)</p>
                  </motion.div>
                ) : result ? (
                  <motion.div key="result" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="ai-scan-result">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                      <div className="rounded-2xl border border-ivory-400 bg-ivory-100 p-5">
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-coal/50 mb-2">Growth Score</div>
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className={`font-display text-5xl font-black leading-none ${scoreColor(result.growth_score)}`} data-testid="ai-scan-growth-score">
                            {result.growth_score}
                          </span>
                          <span className="text-sm text-coal/40">/100</span>
                        </div>
                        <div className="h-2 bg-ivory-300 rounded-full overflow-hidden mb-2">
                          <motion.div initial={{ width: 0 }} animate={{ width: `${result.growth_score}%` }} transition={{ duration: 1, ease: "easeOut" }} className="h-full bg-electric" />
                        </div>
                        <div className="text-xs text-coal/55">{scoreLabel(result.growth_score)}</div>
                      </div>

                      {result.missed_revenue_estimate && (
                        <div data-testid="ai-scan-missed-revenue" className="rounded-2xl border border-signal/25 bg-signal/5 p-5">
                          <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-signal-600 mb-2">
                            <AlertTriangle className="w-3 h-3" /> Geschat gemist
                          </div>
                          <div className="font-display text-xl lg:text-2xl font-black text-coal leading-tight mb-1">{result.missed_revenue_estimate}</div>
                          <div className="text-xs text-coal/50">Op basis van jouw branche &amp; situatie</div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-electric" />
                      <span className="text-xs font-mono uppercase tracking-[0.18em] text-electric">Jouw persoonlijk advies</span>
                    </div>
                    <h3 data-testid="ai-scan-headline" className="font-display text-2xl lg:text-3xl font-black tracking-tight text-coal text-balance leading-tight">
                      {result.headline}
                    </h3>
                    <p data-testid="ai-scan-advice" className="mt-4 text-coal/70 leading-relaxed whitespace-pre-line">{result.advice}</p>

                    {Array.isArray(result.priorities) && result.priorities.length > 0 && (
                      <div className="mt-7">
                        <div className="text-xs font-mono uppercase tracking-[0.18em] text-electric mb-3 flex items-center gap-1.5">
                          <Target className="w-3.5 h-3.5" /> Top prioriteiten
                        </div>
                        <div className="space-y-2">
                          {result.priorities.map((p, i) => (
                            <div key={i} data-testid={`ai-scan-priority-${i}`} className="rounded-xl border border-ivory-400 bg-ivory-100 p-4">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className={`text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${
                                  p.impact === "Hoog" ? "bg-electric/10 text-electric border-electric/25"
                                  : p.impact === "Middel" ? "bg-ivory-300 text-coal/70 border-ivory-400"
                                  : "bg-ivory-200 text-coal/50 border-ivory-400"
                                }`}>Impact: {p.impact}</span>
                                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-coal/40">{p.timeframe}</span>
                              </div>
                              <div className="font-display font-bold text-coal text-sm mb-1">{p.title}</div>
                              <div className="text-xs text-coal/55 leading-relaxed">{p.why}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {Array.isArray(result.next_steps) && result.next_steps.length > 0 && (
                      <div className="mt-6 rounded-2xl border border-electric/20 bg-electric/5 p-5">
                        <div className="flex items-center gap-2 mb-3 text-electric-700">
                          <TrendingUp className="w-4 h-4" />
                          <span className="text-xs font-mono uppercase tracking-[0.18em]">Deze week starten</span>
                        </div>
                        <ol className="space-y-2">
                          {result.next_steps.map((s, i) => (
                            <li key={i} data-testid={`ai-scan-step-${i}`} className="flex gap-3 text-sm text-coal/80">
                              <span className="font-display font-black text-electric shrink-0">{String(i + 1).padStart(2, "0")}</span>
                              <span>{s}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {!captured ? (
                      <div className="mt-8 rounded-2xl border border-electric/25 bg-gradient-to-br from-electric/10 to-electric/[0.02] p-6">
                        {!captureOpen ? (
                          <div className="text-center">
                            <div className="font-display text-lg lg:text-xl font-black text-coal mb-2">Wil je dit volledige rapport in je mailbox?</div>
                            <p className="text-sm text-coal/55 mb-4 max-w-md mx-auto">
                              We sturen je het complete rapport + Tim belt je binnen 24u persoonlijk terug om het door te nemen (geen verkoop-pitch).
                            </p>
                            <button onClick={() => setCaptureOpen(true)} data-testid="ai-scan-open-capture" className="inline-flex items-center gap-2 bg-electric hover:bg-electric-600 text-white font-semibold rounded-full px-6 py-3 transition-all hover:shadow-glow-blue">
                              Stuur mij dit rapport <ArrowRight className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <form onSubmit={submitLead} data-testid="ai-scan-lead-form" className="space-y-3">
                            <div className="font-display text-lg font-black text-coal mb-3">Waar mogen we het rapport naartoe sturen?</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <LeadField icon={User} placeholder="Naam" value={lead.name} onChange={(v) => setLead({ ...lead, name: v })} testid="lead-name" />
                              <LeadField icon={Mail} placeholder="E-mailadres" type="email" value={lead.email} onChange={(v) => setLead({ ...lead, email: v })} testid="lead-email" />
                              <LeadField icon={Phone} placeholder="Telefoon (optioneel)" value={lead.phone} onChange={(v) => setLead({ ...lead, phone: v })} testid="lead-phone" />
                              <LeadField icon={Building2} placeholder="Bedrijf (optioneel)" value={lead.company} onChange={(v) => setLead({ ...lead, company: v })} testid="lead-company" />
                            </div>
                            <label className="flex items-start gap-2 text-xs text-coal/70 pt-2 cursor-pointer">
                              <input type="checkbox" checked={lead.wants_call} onChange={(e) => setLead({ ...lead, wants_call: e.target.checked })} className="mt-0.5 accent-electric" />
                              <span>Ja, Tim mag me binnen 24u persoonlijk bellen om dit door te nemen (gratis, geen verkoop-pitch).</span>
                            </label>
                            <button type="submit" disabled={capturing} data-testid="ai-scan-submit-lead" className="w-full inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-600 disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3 transition-all">
                              {capturing ? (<><Loader2 className="w-4 h-4 animate-spin" /> Versturen…</>) : (<>Stuur mij het rapport <Send className="w-4 h-4" /></>)}
                            </button>
                          </form>
                        )}
                      </div>
                    ) : (
                      <div data-testid="ai-scan-lead-thankyou" className="mt-8 rounded-2xl border border-electric/30 bg-electric/5 p-6 text-center">
                        <div className="w-12 h-12 rounded-full bg-electric/15 grid place-items-center mx-auto mb-3">
                          <CheckCircle2 className="w-5 h-5 text-electric" />
                        </div>
                        <div className="font-display text-xl font-black text-coal mb-2">Het rapport is onderweg!</div>
                        <p className="text-sm text-coal/55 max-w-md mx-auto">
                          Check je inbox (of spam) — Tim neemt binnen 24u persoonlijk contact op om het advies met je door te nemen.
                        </p>
                      </div>
                    )}

                    <div className="mt-6 flex justify-center">
                      <button onClick={reset} data-testid="ai-scan-restart" className="inline-flex items-center justify-center gap-2 text-sm text-coal/50 hover:text-electric transition-colors">
                        <RefreshCw className="w-3.5 h-3.5" /> Opnieuw scannen
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key={`step-${stepIdx}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} data-testid={`ai-scan-step-${currentStep.key}`}>
                    <h3 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-coal mb-2 text-balance">{currentStep.title}</h3>
                    <p className="text-sm text-coal/55 mb-6">{currentStep.subtitle}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {currentStep.options.map((opt, i) => (
                        <motion.button
                          key={opt}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.04 }}
                          onClick={() => choose(opt)}
                          data-testid={`ai-scan-option-${currentStep.key}-${i}`}
                          className="group text-left rounded-2xl border border-ivory-400 bg-ivory-100 hover:bg-electric/10 hover:border-electric/40 px-5 py-4 transition-all flex items-center justify-between"
                        >
                          <span className="text-coal font-medium text-sm sm:text-base">{opt}</span>
                          <ArrowRight className="w-4 h-4 text-coal/30 group-hover:text-electric group-hover:translate-x-0.5 transition-all" />
                        </motion.button>
                      ))}
                    </div>
                    {stepIdx > 0 && (
                      <button onClick={() => setStepIdx(stepIdx - 1)} data-testid="ai-scan-back" className="mt-6 text-sm text-coal/50 hover:text-electric transition-colors">
                        ← Vorige vraag
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const LeadField = ({ icon: Icon, placeholder, value, onChange, type = "text", testid }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-coal/35 pointer-events-none" />
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-white border border-ivory-400 focus:border-electric focus:ring-2 focus:ring-electric/20 focus:outline-none rounded-xl pl-10 pr-4 py-3 text-coal placeholder:text-coal/35 text-sm transition-all"
    />
  </div>
);

export default AiScan;
